import { useCallback, useEffect, useState } from "react";
import {
  fetchPhoto,
  fetchEvents,
  addPhotosToEvent,
  removePhotosFromEvent,
  postPhotoComment,
  deleteComment,
  fullUrl,
  type Comment,
  type EventSummary,
} from "./api.js";
import { useAuth } from "./auth.js";
import { useRecording } from "./recording.js";
import { navigate } from "./router.js";
import { DestinationPicker, noteRecent } from "./destination-picker.js";
import { CommentBody, CommentSourceIcon } from "./comments.js";

/**
 * La visionneuse plein écran : une photo, ses albums, son fil de commentaires.
 */
export type ViewerPhoto = { id: string; scanName?: string; flickrDescription?: string };

export function PhotoViewer({
  photos,
  index,
  eventId,
  albumTitle,
  onIndex,
  onClose,
  onAssigned,
}: {
  photos: ViewerPhoto[];
  index: number;
  /** L'album depuis lequel on regarde, s'il y en a un (null depuis « À trier »). */
  eventId: string | null;
  albumTitle?: string;
  onIndex: (i: number) => void;
  onClose: () => void;
  onAssigned?: () => void;
}) {
  const photo = photos[index]!;
  const { noteView, dataVersion, status, refreshData } = useRecording();
  const { state: authState } = useAuth();
  const isOwner = authState.status === "authed" && authState.isOwner;
  /** Ranger cette photo (l'ajouter à un album, l'en retirer) : réservé aux éditeurs. */
  const peutRanger = authState.status === "authed" && authState.isEditor;
  const [comments, setComments] = useState<Comment[]>([]);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [albums, setAlbums] = useState<EventSummary[]>([]);
  const [destinations, setDestinations] = useState<EventSummary[]>([]);

  const go = useCallback(
    (delta: number) => {
      const next = index + delta;
      if (next >= 0 && next < photos.length) onIndex(next);
    },
    [index, photos.length, onIndex],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      if (el && (el.tagName === "TEXTAREA" || el.tagName === "INPUT")) {
        if (e.key === "Escape") (el as HTMLElement).blur();
        return; // ne pas naviguer pendant la saisie
      }
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  // Attribution de la voix à cette photo tant qu'elle est ouverte.
  useEffect(() => {
    noteView(eventId, photo.id);
    return () => noteView(eventId, null);
  }, [photo.id, eventId, noteView]);

  // Charge le fil de commentaires de la photo (rafraîchi après transcription).
  useEffect(() => {
    let cancelled = false;
    fetchPhoto(photo.id)
      .then((d) => {
        if (cancelled) return;
        setComments(d.comments);
        setAlbums(d.albums);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [photo.id, dataVersion]);

  const postComment = async () => {
    const t = draft.trim();
    if (!t) return;
    setPosting(true);
    try {
      const c = await postPhotoComment(photo.id, t);
      setComments((cs) => [...cs, c]);
      setDraft("");
    } finally {
      setPosting(false);
    }
  };

  const removeComment = async (id: string) => {
    await deleteComment(id);
    setComments((cs) => cs.filter((c) => c.id !== id));
  };

  /**
   * Fermer au clic « à côté », y compris dans le panneau de commentaires.
   *
   * Le panneau fait partie de la fenêtre, donc un clic dedans ne remontait pas jusqu'au
   * fond : le vide sous le dernier commentaire appartient au `<ul>` (il a `flex: 1` pour
   * remplir la hauteur), pas au décor. On ferme donc dès que le clic atterrit sur quelque
   * chose d'**inerte** — ni bouton, ni champ, ni lien, ni `role="button"` : le texte d'un
   * commentaire en est un, puisqu'il s'édite au clic.
   *
   * `stopPropagation` inconditionnel : sans lui, le clic remonterait aussi au fond qui
   * appelle `onClose` de son côté, et fermer deux fois ferait deux retours d'historique.
   */
  const closeIfInert = (e: React.MouseEvent): void => {
    e.stopPropagation();
    const cible = e.target as HTMLElement;
    if (!cible.closest('button, a, input, textarea, select, [role="button"], [contenteditable]')) {
      onClose();
    }
  };

  return (
    <div className="viewer viewer--annot" data-testid="photo-viewer" onClick={onClose}>
      {/*
        `stopPropagation` : le bouton est un enfant direct du fond, qui ferme aussi au clic.
        Sans lui, `onClose` partait deux fois et on remontait de deux crans d'historique —
        depuis « À trier », on atterrissait sur la page d'accueil au lieu de revenir à la
        grille. Les flèches de navigation avaient déjà ce garde-fou.
      */}
      <button
        className="viewer__close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Fermer"
      >
        ×
      </button>
      {index > 0 && (
        <button
          className="viewer__nav viewer__nav--prev"
          onClick={(e) => { e.stopPropagation(); go(-1); }}
          aria-label="Précédente"
        >‹</button>
      )}
      <div className="viewer__main" onClick={closeIfInert}>
        {/* La photo elle-même ne ferme pas : « en dehors de la photo » veut dire dehors. */}
        <div className="viewer__imgwrap" onClick={(e) => e.stopPropagation()}>
          <img className="viewer__img" src={fullUrl(photo.id)} alt={photo.flickrDescription || photo.scanName || ""} />
          <span className="viewer__pos">{index + 1} / {photos.length}</span>
        </div>
        <aside className="viewer__panel">
          <div className="viewer__meta" data-testid="viewer-meta">
            <span className="viewer__album">{albumTitle ?? "À trier"}</span>
            {photo.scanName && <span className="viewer__scan">{photo.scanName}</span>}
          </div>

          {/* Tous les albums qui contiennent cette photo — elle peut être dans plusieurs.
              Celui d'où l'on regarde est mis en évidence, et se retire d'un clic. */}
          <div className="palb" data-testid="photo-albums">
            {albums.map((a) => (
              <span
                key={a.id}
                className={`palb__chip ${a.id === eventId ? "palb__chip--current" : ""}`}
              >
                <button
                  className="palb__open"
                  onClick={() => navigate({ kind: "album", id: a.id, photoId: null })}
                  title={`Ouvrir « ${a.title || "Sans titre"} »`}
                >
                  {a.title || "Sans titre"}
                </button>
                {peutRanger && (
                  <button
                    className="palb__del"
                    aria-label={`Retirer de « ${a.title || "Sans titre"} »`}
                    title={`Retirer de « ${a.title || "Sans titre"} »`}
                    onClick={() => {
                      void removePhotosFromEvent(a.id, [photo.id]).then(() => {
                        refreshData();
                        if (a.id === eventId) onClose();
                        else setAlbums((xs) => xs.filter((x) => x.id !== a.id));
                      });
                    }}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {albums.length === 0 && <span className="palb__none">Dans aucun album</span>}
            {peutRanger && (
              <button
                className="palb__add"
                onClick={() => {
                  void fetchEvents().then((d) => {
                    setDestinations(d.events);
                    setAssigning(true);
                  });
                }}
                data-testid="photo-assign"
              >
                ＋ Ajouter à un album
              </button>
            )}
          </div>
          <div className="viewer__label">Commentaires</div>
          {status === "recording" && (
            <p className="viewer__rechint" data-testid="photo-rec-hint">
              <span className="rec__dot" /> La voix sera attribuée à cette photo
            </p>
          )}
          <ul className="cthread" data-testid="cthread">
            {comments.map((c) => (
              <li key={c.id} className="cmt" data-testid="cmt">
                <CommentSourceIcon source={c.source} />
                <CommentBody
                  comment={c}
                  onEdited={(u) => setComments((cs) => cs.map((x) => (x.id === u.id ? u : x)))}
                />
                {isOwner && (
                  <button className="cmt__del" onClick={() => void removeComment(c.id)} aria-label="Supprimer">
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
          <div className="cadd">
            <input
              className="cadd__input"
              value={draft}
              placeholder="Ajouter un commentaire…"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void postComment();
              }}
              data-testid="cmt-input"
            />
            <button
              className="cadd__btn"
              disabled={posting || !draft.trim()}
              onClick={() => void postComment()}
              data-testid="cmt-post"
            >
              Envoyer
            </button>
          </div>
        </aside>
      </div>
      {index < photos.length - 1 && (
        <button
          className="viewer__nav viewer__nav--next"
          onClick={(e) => { e.stopPropagation(); go(1); }}
          aria-label="Suivante"
        >›</button>
      )}
      {assigning && destinations.length > 0 && (
        <DestinationPicker
          events={destinations}
          photoIds={[photo.id]}
          onPick={(evId) => {
            void addPhotosToEvent(evId, [photo.id]).then(async () => {
              noteRecent(evId);
              setAssigning(false);
              // La photo gagne un album : on rafraîchit ses appartenances sur place.
              setAlbums((await fetchPhoto(photo.id)).albums);
              refreshData();
              onAssigned?.();
            });
          }}
          onClose={() => setAssigning(false)}
        />
      )}
    </div>
  );
}
